import { useState } from 'react';

import { useSelector } from '../../../shared/utils';
import {
  createSamlProvider,
  getSamlProviderDetails,
  updateSamlProvider,
} from '../data-sources';
import { type ProviderHookType, type SamlProvider } from '../types';

const { protocol, host: domain } = window.location;

function getDefaultSamlProvider(realmName: string): SamlProvider {
  return {
    name: '',
    alias: '',
    protocol: 'saml',
    spEntityId: `${protocol}//${domain}/auth/realms/${realmName}`,
    isEnabled: false,
    isTestMode: false,
    entityDescriptorUrl: '',
    wantsAssertionsSigned: true,
    wantsAssertionsEncrypted: false,
    shouldForceAuthentication: false,
    shouldValidateSignatures: true,
    shouldSignSPMetadata: false,
    wantSingleLogout: false,
    logoutServiceUrl: '',
    wantsAuthRequestsSigned: false,
    nameIDPolicyFormat: '',
  };
}

/** Hook for working with SAMLEditView */
export function useSamlEditView(): ProviderHookType<SamlProvider> {
  const mtaRealmName = useSelector((s) => s.user.domainInfo.MTA_REALM_NAME);
  const [provider, setProvider] = useState<SamlProvider>(
    getDefaultSamlProvider(mtaRealmName)
  );
  const [isNew, setIsNew] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  return {
    provider,
    setProvider,
    canSave,
    setCanSave,
    isNew,
    isLoading,
    isSaving,
    error,
    newProvider: () => {
      if (isLoading || isSaving) {
        return;
      }

      setIsNew(true);
      setError('');

      setProvider(getDefaultSamlProvider(mtaRealmName));
    },
    loadProvider: (alias: string) => {
      if (isLoading || isSaving) {
        return;
      }

      setIsNew(false);
      setIsLoading(true);
      setError('');

      getSamlProviderDetails(alias)
        .then((data) => {
          setProvider(data);
        })
        .catch(() => {
          setError('Failed to load provider details');
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    saveProvider: (): Promise<void> => {
      if (isLoading || isSaving) {
        return Promise.reject();
      }

      setIsSaving(true);
      setError('');

      const saveFunction = isNew ? createSamlProvider : updateSamlProvider;

      return saveFunction(provider)
        .then(() => {
          setIsNew(false);
          setIsSaving(false);
        })
        .catch(() => {
          setError('Failed to save provider');
          setIsSaving(false);

          return Promise.reject();
        });
    },
  };
}
